/**
 * One sentence, one place.
 *
 * Every career in the registry is classified against NCO-2015 (India's National
 * Classification of Occupations) and ISCO-08 (the ILO's international
 * standard). That is what stops the corpus being a list somebody invented, so
 * it is worth saying out loud wherever a person is looking at careers — but it
 * should read the same way everywhere, hence a single component rather than
 * eight copies of the sentence drifting apart.
 *
 * The "What this means" trigger opens a real dialog (focus trapped, Escape
 * closes, labelled and described) so somebody can read the definitions and the
 * official citations without losing their place in a long list.
 */
import { ExternalLink, Landmark } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  OCCUPATION_STANDARDS_SENTENCE,
  OCCUPATION_STANDARD_SOURCES,
} from "@/lib/occupationStandards";

export { OCCUPATION_STANDARDS_SENTENCE };

function StandardsDialog({ triggerClassName = "" }: { triggerClassName?: string }) {
  return (
    <Dialog>
      <DialogTrigger
        className={`inline-flex min-h-11 items-center rounded-full px-1 text-xs font-medium text-primary underline underline-offset-2 transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${triggerClassName}`}
      >
        What this means
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-3xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Occupation standards</DialogTitle>
          <DialogDescription>
            The two classifications behind every role you see, and where to check them.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-4">
          {OCCUPATION_STANDARD_SOURCES.map((s) => (
            <li key={s.id} className="rounded-2xl border border-border/60 bg-muted/20 p-4">
              <p className="font-display text-sm font-semibold text-foreground">
                {s.code} — {s.name}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{s.custodian}</p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{s.summary}</p>
              <a
                href={s.href}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-3 inline-flex min-h-9 items-center gap-1.5 text-xs font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {s.hrefLabel}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>

        <p className="text-xs leading-relaxed text-muted-foreground">
          Each listing shows the basis applied to it, so you can check the classification behind a
          role rather than take our word for it.{" "}
          <Link
            to="/occupation-standards"
            className="font-medium text-primary underline underline-offset-2"
          >
            Read the full methodology
          </Link>
          .
        </p>
      </DialogContent>
    </Dialog>
  );
}

export function OccupationStandardsNote({
  variant = "inline",
  className = "",
}: {
  /** `inline` is a plain line of small print; `card` gets a border and icon. */
  variant?: "inline" | "card";
  className?: string;
}) {
  if (variant === "card") {
    return (
      <div
        className={`flex items-start gap-3 rounded-2xl border border-border/60 bg-muted/20 p-4 ${className}`}
      >
        <Landmark className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <div className="space-y-2">
          <p className="text-xs leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground">Classified, not invented.</span>{" "}
            {OCCUPATION_STANDARDS_SENTENCE} So a role here means the same thing to a school, an
            employer and a labour ministry.
          </p>
          <StandardsDialog />
        </div>
      </div>
    );
  }

  return (
    <p className={`text-xs leading-relaxed text-muted-foreground ${className}`}>
      {OCCUPATION_STANDARDS_SENTENCE} <StandardsDialog />
    </p>
  );
}

export default OccupationStandardsNote;
